// Dirty-flag renderer (spec §5.1). Draws in requestAnimationFrame only when a
// layer is dirty, so an idle board costs nothing. Objects being manipulated
// ("active") move to the interactive layer so a drag never repaints the static
// layer. Text comes from the raster cache (see text-cache.ts), whose
// time-budgeted work may ask for follow-up frames until everything is sharp.
import type { BoardObject } from '../../shared/types.ts';
import type { Store } from '../model/store.ts';
import type { Camera } from './camera.ts';
import type { Layers } from './layers.ts';
import type { SpatialIndex } from './spatial-index.ts';
import { intersects, objectMatrix, type Box } from '../util/geometry.ts';
import { drawGreeked, drawText, type TextLayouts } from '../text/placement.ts';
import { RASTER_PAD, type TextCache } from './text-cache.ts';
import type { ConnectorGeometry } from './connectors.ts';
import type { ImageCache } from './images.ts';
import { drawObject, type DrawContext } from './draw/index.ts';
import { drawConnector } from './draw/content.ts';

export const LOD_ZOOM = 0.3;
const BACKGROUND = '#F7F7F5';
const GRID_DOT = '#D9D9D6';
const GRID_WORLD_SPACING = 32;
const GRID_MIN_PX = 16; // CSS px between dots, at least
/** Per-frame time for text layout + rasterising, measured from frame start. */
const TEXT_BUDGET_MS = 8;
const RECENT_FRAMES = 120;

export interface RenderStats {
  frames: number;       // total frames drawn
  lastFrameMs: number;
  maxFrameMs: number;   // reset by the reader
  drawn: number;        // objects drawn in the last static frame
  total: number;
  textPending: number;  // text still waiting for layout/raster after the last frame
  recent: number[];     // last RECENT_FRAMES frame times (ms)
}

export interface RendererDeps {
  layers: Layers;
  camera: Camera;
  store: Store;
  index: SpatialIndex;
  layouts: TextLayouts;
  textCache: TextCache;
  connectors: ConnectorGeometry;
  images: ImageCache;
  /** Draws selection UI etc. on the interactive layer after active objects. */
  drawOverlay: (ctx: CanvasRenderingContext2D) => void;
  /** Someone who can edit is looking: draw editing affordances ("+ Add card"). */
  editable: boolean;
}

export class Renderer {
  readonly stats: RenderStats = { frames: 0, lastFrameMs: 0, maxFrameMs: 0, drawn: 0, total: 0, textPending: 0, recent: [] };
  private readonly d: RendererDeps;
  private staticDirty = true;
  private interactiveDirty = true;
  private scheduled = false;
  private active = new Set<string>();
  private gridPattern: CanvasPattern | null = null;
  private gridTileSize = 0;
  private editing: string | null = null;

  constructor(deps: RendererDeps) {
    this.d = deps;
    this.schedule();
  }

  get activeIds(): ReadonlySet<string> { return this.active; }

  invalidate(): void {
    this.staticDirty = this.interactiveDirty = true;
    this.schedule();
  }

  invalidateInteractive(): void {
    this.interactiveDirty = true;
    this.schedule();
  }

  /** Moves these objects to the interactive layer (empty = none). */
  setActive(ids: Iterable<string>): void {
    const next = new Set(ids);
    if (next.size === this.active.size && [...next].every((id) => this.active.has(id))) return;
    this.active = next;
    this.invalidate();
  }

  /** Hides this object's canvas text while it's being edited in the DOM. */
  setEditing(id: string | null): void {
    if (id === this.editing) return;
    this.editing = id;
    this.invalidate();
  }

  objectsChanged(ids: ReadonlySet<string>): void {
    for (const id of ids) {
      if (this.active.has(id)) this.interactiveDirty = true;
      else this.staticDirty = true;
      if (this.staticDirty && this.interactiveDirty) break;
    }
    this.schedule();
  }

  /** World → device pixels. */
  setWorldTransform(ctx: CanvasRenderingContext2D): void {
    const { camera, layers } = this.d;
    const s = camera.zoom * layers.dpr;
    ctx.setTransform(s, 0, 0, s, -camera.x * s, -camera.y * s);
  }

  /** Box-local (origin at unrotated top-left) → device pixels, without save/restore. */
  setObjectTransform(ctx: CanvasRenderingContext2D, o: Box): void {
    const { camera, layers } = this.d;
    const s = camera.zoom * layers.dpr;
    const [a, b, c, d, e, f] = objectMatrix(o, s, -camera.x * s, -camera.y * s);
    ctx.setTransform(a, b, c, d, e, f);
  }

  private schedule(): void {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(this.frame);
  }

  private frame = (): void => {
    this.scheduled = false;
    if (!this.staticDirty && !this.interactiveDirty) return;
    const t0 = performance.now();
    const text = this.d.textCache;
    text.beginFrame(TEXT_BUDGET_MS);
    let staticPending = 0;
    if (this.staticDirty) {
      this.staticDirty = false;
      this.drawStatic();
      staticPending = text.pending;
    }
    if (this.interactiveDirty) {
      this.interactiveDirty = false;
      this.drawInteractive();
    }
    // Text that didn't fit this frame's budget gets another frame.
    if (staticPending) this.staticDirty = true;
    if (text.pending > staticPending) this.interactiveDirty = true;

    const ms = performance.now() - t0;
    const st = this.stats;
    st.frames++;
    st.lastFrameMs = ms;
    st.maxFrameMs = Math.max(st.maxFrameMs, ms);
    st.textPending = text.pending;
    st.recent.push(ms);
    if (st.recent.length > RECENT_FRAMES) st.recent.shift();
    if (this.staticDirty || this.interactiveDirty) this.schedule();
  };

  /** Visible objects in paint order, optionally restricted to / excluding the active set. */
  private visible(activeOnly: boolean): BoardObject[] {
    const { camera, layers, store, index } = this.d;
    const view = camera.viewport(layers.width, layers.height);
    if (activeOnly) {
      const objs: BoardObject[] = [];
      for (const id of this.active) {
        const o = store.get(id);
        const b = index.getBounds(id);
        if (o && b && intersects(b, view)) objs.push(o);
      }
      return objs.sort((a, b) => store.rank(a.id) - store.rank(b.id));
    }

    const ids = index.query(view);
    const out: BoardObject[] = [];
    if (ids.length > store.size / 2) {
      // Most of the board is visible: walking the global order beats sorting.
      const visible = new Set(ids);
      for (const id of store.orderedIds()) {
        if (visible.has(id) && !this.active.has(id)) out.push(store.get(id)!);
      }
      return out;
    }
    const ranked: [number, BoardObject][] = [];
    for (const id of ids) {
      if (this.active.has(id)) continue;
      const o = store.get(id);
      if (o) ranked.push([store.rank(id), o]);
    }
    ranked.sort((a, b) => a[0] - b[0]);
    for (const [, o] of ranked) out.push(o);
    return out;
  }

  private drawStatic(): void {
    const { layers, store } = this.d;
    const ctx = layers.staticCtx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = BACKGROUND;
    ctx.fillRect(0, 0, layers.staticCanvas.width, layers.staticCanvas.height);
    this.drawGrid(ctx);

    const objs = this.visible(false);
    ctx.imageSmoothingQuality = 'medium';
    const dc = this.drawContext();
    for (const o of objs) this.drawOne(ctx, o, dc);
    this.stats.drawn = objs.length;
    this.stats.total = store.size;
  }

  private drawInteractive(): void {
    const { layers } = this.d;
    const ctx = layers.interactiveCtx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, layers.interactiveCanvas.width, layers.interactiveCanvas.height);
    const dc = this.drawContext();
    for (const o of this.visible(true)) this.drawOne(ctx, o, dc);
    this.setWorldTransform(ctx);
    this.d.drawOverlay(ctx);
  }

  private drawContext(): DrawContext {
    const { camera, layers } = this.d;
    return { lod: camera.zoom < LOD_ZOOM, zoom: camera.zoom, scale: camera.zoom * layers.dpr, images: this.d.images, editable: this.d.editable };
  }

  private drawOne(ctx: CanvasRenderingContext2D, o: BoardObject, dc: DrawContext): void {
    if (o.type === 'connector') {
      this.setWorldTransform(ctx);
      drawConnector(ctx, o, this.d.connectors.shape(o).points, dc.lod, dc.scale);
      return;
    }
    this.setObjectTransform(ctx, o);
    drawObject(ctx, o, dc);
    this.drawObjectText(ctx, o, dc.lod);
  }

  /** Draws `o`'s text with the object transform already set. */
  private drawObjectText(ctx: CanvasRenderingContext2D, o: BoardObject, lod: boolean): void {
    if (o.id === this.editing) return;
    const { layouts, textCache } = this.d;
    // Below LOD zoom stickies and shapes show no text (spec §5.1); text objects are greeked.
    if (lod && o.type !== 'text') return;

    let placed = layouts.peek(o);
    if (placed === undefined) {
      if (!textCache.canWork()) {
        textCache.pending++;
        return;
      }
      textCache.noteWork();
      placed = layouts.get(o);
    }
    if (!placed) return;
    if (lod) return drawGreeked(ctx, placed);

    const scale = this.d.camera.zoom * this.d.layers.dpr;
    const d = textCache.get(o.id, placed, scale);
    if (d.kind === 'raster') {
      const { raster, bucket } = d;
      const pad = RASTER_PAD / bucket;
      ctx.drawImage(raster.image, placed.region.x - pad, placed.region.y - pad, raster.width / bucket, raster.height / bucket);
    } else if (d.kind === 'vector') {
      const r = placed.region;
      ctx.save();
      ctx.beginPath();
      ctx.rect(r.x, r.y, r.w, r.h);
      ctx.clip();
      drawText(ctx, placed, scale);
      ctx.restore();
    }
  }

  /**
   * Dot grid as a repeating pattern: one fill per frame regardless of density.
   * The tile is rebuilt at the current device-pixel spacing so dots stay a
   * constant size; spacing doubles/halves to stay within a comfortable range.
   */
  private drawGrid(ctx: CanvasRenderingContext2D): void {
    const { camera, layers } = this.d;
    const dpr = layers.dpr;
    let spacing = GRID_WORLD_SPACING * camera.zoom * dpr;
    while (spacing < GRID_MIN_PX * dpr) spacing *= 2;
    while (spacing > GRID_MIN_PX * 4 * dpr) spacing /= 2;
    const size = Math.round(spacing);
    if (!this.gridPattern || this.gridTileSize !== size) {
      const tile = new OffscreenCanvas(size, size);
      const t = tile.getContext('2d')!;
      t.fillStyle = GRID_DOT;
      t.beginPath();
      t.arc(dpr, dpr, dpr, 0, Math.PI * 2);
      t.fill();
      this.gridPattern = ctx.createPattern(tile, 'repeat');
      this.gridTileSize = size;
      if (!this.gridPattern) return;
    }
    const mod = (v: number) => ((v % size) + size) % size;
    const ox = mod(-camera.x * camera.zoom * dpr) - dpr;
    const oy = mod(-camera.y * camera.zoom * dpr) - dpr;
    this.gridPattern.setTransform(new DOMMatrix().translate(ox, oy));
    ctx.fillStyle = this.gridPattern;
    ctx.fillRect(0, 0, layers.staticCanvas.width, layers.staticCanvas.height);
  }
}
